import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaHome, FaUsers, FaChalkboardTeacher, FaSignOutAlt, FaBars, FaClipboardList, FaClipboardCheck } from 'react-icons/fa';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, role } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isMccShell = role === 'mcc';
  const isIndustryShell = role === 'industry';
  const isItiShell = role === 'iti';

  const isActive = (path) => {
    if (path === '/industry/dashboard') {
      return location.pathname === path || location.pathname === '/industry/approve';
    }
    if (path === '/iti-home') {
      return location.pathname === path;
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    const role = logout();

    if (role === "admin") {
      navigate("/admin"); // admin login page
      return;
    }

    if (role === "mcc") {
      navigate("/mcc"); // mcc login page
      return;
    }

    navigate("/");
  };

  // Nav item helper to avoid repetition (use `iconSrc` for custom assets e.g. MCC mapping icon)
  // `activeImgClassName`: optional Tailwind for <img> when selected (e.g. white asset → dark on white tab)
  const NavItem = ({ path, icon: Icon, iconSrc, label, iconSize, activeImgClassName }) => (
    <div
      onClick={() => {
        navigate(path);
        setIsMobileOpen(false);
      }}
      className={`group flex items-center gap-3 px-[18px] py-[14px] mb-2 cursor-pointer transition-colors duration-300
        ${isActive(path)
          ? 'bg-white text-[#273142] font-semibold'
          : isMccShell
            ? 'text-[#F1F5F9] hover:bg-white/10 active:bg-white/20 hover:text-white'
            : 'text-gray-300 hover:bg-white/10 active:bg-white/20 hover:text-white'
        }`}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          width={30}
          height={32}
          className={`shrink-0 object-contain transition-transform duration-300
            ${isActive(path)
              ? activeImgClassName ?? ''
              : 'opacity-95 group-hover:opacity-100 group-hover:scale-110 active:scale-105 [filter:drop-shadow(0_0_1px_rgba(255,255,255,0.35))]'}
          `}
        />
      ) : (
        <Icon
          {...(iconSize != null ? { size: iconSize } : {})}
          className={`transition-transform duration-300 shrink-0
            ${isActive(path)
              ? 'text-[#273142]'
              : 'group-hover:scale-110 active:scale-105'
            }`}
        />
      )}
      <span className="transition-transform duration-300 group-hover:translate-x-1 active:translate-x-[2px]">
        {label}
      </span>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f4f6f8] overflow-hidden">
      {/* Backdrop for Mobile */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[150] md:hidden transition-opacity duration-300"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen w-[260px] ${isMccShell ? 'bg-[#12314c] text-[#F1F5F9]' : 'bg-primary text-white'} pt-6 py-6 flex flex-col z-[400]
        transition-transform transition-opacity duration-300 ease-in-out
        md:translate-x-0 ${isMobileOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 md:opacity-100'}
      `}>
        {/* Sidebar Header */}
        <div className="flex justify-between items-center mb-8 px-3">
          <div className="flex items-center gap-3">
            {!isMccShell && (
              <img
                src="/static/media/Emblem_of_Haryana.svg.31b58c412963903c3a93.png"
                alt="Logo"
                className="w-20 brightness-0 invert transition-transform duration-300 hover:scale-105"
              />
            )}
            <span className={`font-bold text-lg ${isMccShell ? 'text-[#F1F5F9]' : ''}`}>
              {isMccShell ? (
                'Welcome MCC!'
              ) : isIndustryShell ? (
                <>
                  Welcome,<br /> Industry!
                </>
              ) : isItiShell ? (
                <>
                  Welcome,<br /> ITI!
                </>
              ) : (
                <>
                  Welcome ,<br /> DSDIT Admin!
                </>
              )}
            </span>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1">
          {role === 'mcc' ? (
            <>
              <NavItem
                path="/mcc/mapping"
                iconSrc={
                  isActive("/mcc/mapping")
                    ? `${process.env.PUBLIC_URL || ''}/icons/mappingIcon.png`
                    : `${process.env.PUBLIC_URL || ''}/icons/mappingwhiteIcon.png`
                }  
                label="Mapping"
              />
              <NavItem path="/mcc/student" 
                 iconSrc={
                  isActive("/mcc/student")
                    ? `${process.env.PUBLIC_URL || ''}/icons/StudentIcon.png`
                    : `${process.env.PUBLIC_URL || ''}/icons/StudentIconWhite.png`
                } 
                label="Student" 
              />
              <NavItem
              path="/mcc/industry"
              iconSrc={ isActive("/mcc/industry")
                    ? `${process.env.PUBLIC_URL || ''}/icons/IndustryIcon.png`
                    : `${process.env.PUBLIC_URL || ''}/icons/Industrylogowhite.png`
                }              
                label="Industry"
              />

              <NavItem path="/mcc/iti" 
                iconSrc={ isActive("/mcc/iti")
                      ? `${process.env.PUBLIC_URL || ''}/icons/ITILogo.png`
                      : `${process.env.PUBLIC_URL || ''}/icons/ITILogoWhite.png`
                  }
                label="ITI"  
              />
              <NavItem
              path="/mcc/trade"
              iconSrc={ isActive("/mcc/trade")
                    ? `${process.env.PUBLIC_URL || ''}/icons/TradeIcon.png`
                    : `${process.env.PUBLIC_URL || ''}/icons/TradeIconWhite.png`
                }              
                label="Trade"
              />
              <NavItem
                path="/mcc/trainer"
                iconSrc={`${process.env.PUBLIC_URL || ''}/icons/trainerWhite.png`}
                activeImgClassName="brightness-0"
                label="Trainer"
              />
            </>
          ) : role === 'industry' ? (
            <>
              <NavItem path="/industry/dashboard" icon={FaClipboardList} label="Attendance" />
              <NavItem path="/industry/approved-attendance" icon={FaClipboardCheck} label="Approved Attendance" />
            </>
          ) : role === 'iti' ? (
            <>
              <NavItem path="/iti-home" icon={FaClipboardList} label="Attendance" />
              <NavItem path="/iti/approve-attendance" icon={FaClipboardCheck} label="Approve Attendance" />
            </>
          ) : (
            <>
              <NavItem path="/admin/dashboard" icon={FaHome} label="Dashboard" />
              <NavItem path="/admin/attendance" icon={FaUsers} label="Students Attendance" />
              <NavItem path="/admin/instructors" icon={FaChalkboardTeacher} label="Trainers" />
            </>
          )}
        </nav>

        {/* Logout Section */}
        <div
          className={`mt-auto flex items-center gap-3 px-[18px] py-[14px] cursor-pointer transition-all duration-300 rounded-lg
            ${isMccShell
              ? 'text-[#F1F5F9] hover:bg-white/10 hover:text-white active:bg-white/15'
              : 'text-red-300 hover:bg-red-500/10 hover:text-red-400 active:bg-red-500/20'}`}
          onClick={handleLogout}
        >
          <FaSignOutAlt className="transition-transform duration-300 hover:scale-110 active:scale-105" />
          <span>Logout</span>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 md:ml-[260px]">
        {/* Mobile Navbar Header */}
        <header className="md:hidden flex items-center p-4">
          <button
            className="p-2 text-[#273142] transition-transform duration-300 hover:scale-110 active:scale-105"
            onClick={() => setIsMobileOpen(true)}
            aria-label="Open Menu"
          >
            <FaBars size={24} />
          </button>
        </header>

        {/* Content Wrapper */}
        <div
          key={location.pathname}
          className="p-6 md:p-8 animate-[fadeSlideIn_0.25s_ease-out]"
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;